import React, { useState, useEffect } from 'react';
import Seo from '../components/Seo';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, FileText, Filter } from 'lucide-react';
import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';
import { products, categories } from '../mock/mock';

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = searchParams.get('category') || 'all';
  const [active, setActive] = useState(initial);

  useEffect(() => {
    setActive(searchParams.get('category') || 'all');
  }, [searchParams]);

  const filtered = active === 'all' ? products : products.filter((p) => p.category === active);

  const setFilter = (id) => {
    setActive(id);
    if (id === 'all') setSearchParams({});
    else setSearchParams({ category: id });
  };

  return (
    <div>
      <Seo title="Rice & Grain Products — Pakistan Rice Exporter" description="Explore KTC's export range: 1121 & 1509 basmati, IRRI-6 & PK-386 non-basmati rice, Himalayan Pink Salt, refined salt, milling wheat, yellow corn maize and white hulled sesame seeds." path="/products" />
      <PageHero
        title="Our Products"
        subtitle="Premium rice, Himalayan Pink Salt, wheat, yellow corn maize and natural white hulled sesame seeds, milled, graded and packed to international standards."
        crumb="Products"
      />

      <section className="max-w-7xl mx-auto px-6 py-14">
        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap mb-10">
          <span className="flex items-center gap-1.5 text-ktc-charcoal/60 text-sm mr-2"><Filter size={15} /> Filter:</span>
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${active === 'all' ? 'bg-ktc-green text-ktc-ivory' : 'bg-ktc-cream/70 text-ktc-charcoal/75 hover:bg-ktc-cream'}`}
          >
            All Products
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              onClick={() => setFilter(c.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${active === c.id ? 'bg-ktc-green text-ktc-ivory' : 'bg-ktc-cream/70 text-ktc-charcoal/75 hover:bg-ktc-cream'}`}
            >
              {c.name}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((p, i) => (
            <Reveal key={p.id} delay={(i % 3) * 60}>
              <div className="group bg-ktc-ivoryLight rounded-xl overflow-hidden border border-ktc-cream card-lift h-full flex flex-col">
                <Link to={`/products/${p.slug}`} className="img-zoom h-52 relative block">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                  {p.tag && <span className="absolute top-3 left-3 bg-ktc-gold text-ktc-greenDark text-[11px] font-semibold px-2.5 py-1 rounded-full">{p.tag}</span>}
                </Link>
                <div className="p-6 flex flex-col flex-1">
                  <span className="text-[11px] uppercase tracking-wider text-ktc-gold font-semibold">{categories.find((c) => c.id === p.category)?.name}</span>
                  <h3 className="font-serif text-2xl font-semibold text-ktc-green mt-1 leading-snug">{p.name}</h3>
                  <p className="text-sm text-ktc-charcoal/70 mt-2 flex-1">{p.short}</p>
                  <div className="flex items-center gap-3 mt-5">
                    <Link to={`/products/${p.slug}`} className="inline-flex items-center gap-1.5 text-ktc-green font-semibold text-sm group-hover:gap-2.5 transition-all">
                      Details <ArrowRight size={15} />
                    </Link>
                    <Link to={`/quote?product=${encodeURIComponent(p.name)}`} className="ml-auto inline-flex items-center gap-1.5 bg-ktc-green/10 hover:bg-ktc-green hover:text-ktc-ivory text-ktc-green text-sm font-medium px-3.5 py-2 rounded-md transition-colors">
                      <FileText size={15} /> Enquire
                    </Link>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {filtered.length === 0 && (
          <p className="text-center text-ktc-charcoal/60 py-16">No products in this category yet.</p>
        )}
      </section>
    </div>
  );
};

export default Products;
